import type { OptionRow } from '../services/gexCalculator';
import type {
  DteBucketRow,
  Tier1GexMetrics,
  Tier2Metrics,
  VolAnalytics,
  VolFlowMetrics,
} from '../types/marketAnalytics';

/** Tier-2 literature metrics (module 5–7 proxies, display only). */
export function buildTier2Metrics(
  gex: Tier1GexMetrics,
  volFlow: VolFlowMetrics,
  vol: VolAnalytics,
  dteBuckets: DteBucketRow[],
  rows: OptionRow[],
): Tier2Metrics {
  let dealer_constraint: Tier2Metrics['dealer_constraint'] = 'normal';
  if (gex.trap_zone || (gex.gex_zscore != null && gex.gex_zscore < -1.2)) {
    dealer_constraint = 'strained';
  } else if (gex.gex_zscore != null && gex.gex_zscore > 0.8 && vol.intraday_regime === 'morning') {
    dealer_constraint = 'tight';
  }

  let buyPrem = 0;
  let sellPrem = 0;
  for (const row of rows) {
    const prem = Number(row.Premium ?? row.premium ?? 0) || 0;
    const side = String(row.Side ?? row.side ?? '').toLowerCase();
    if (side.includes('buy')) buyPrem += prem;
    else if (side.includes('sell')) sellPrem += prem;
  }
  const totalPrem = buyPrem + sellPrem;
  const opening_drive =
    totalPrem > 0 ? (buyPrem - sellPrem) / totalPrem : volFlow.signed_buyer_pcr != null
      ? (volFlow.signed_buyer_pcr - 1) / (volFlow.signed_buyer_pcr + 1)
      : null;

  const zeroDte = dteBuckets.find((b) => b.bucket === '0-4d');
  const theta_cliff_minutes =
    zeroDte && zeroDte.contract_count > 0 && gex.minutes_to_close > 0
      ? Math.max(5, Math.round(gex.minutes_to_close * 0.45))
      : null;

  const gamma_theta_crossover_near = Boolean(
    zeroDte &&
      zeroDte.contract_count > 0 &&
      gex.minutes_to_close < 180 &&
      gex.gamma_acceleration != null &&
      gex.gamma_acceleration > 0.5,
  );

  const shortBucket = dteBuckets.find((b) => b.bucket === '0-4d');
  const cwr_proxy =
    shortBucket && shortBucket.open_interest > 0
      ? Math.min(0.45, Math.abs(shortBucket.net_gex) / (shortBucket.open_interest * 100))
      : null;

  return {
    dealer_constraint,
    opening_drive,
    theta_cliff_minutes,
    gamma_theta_crossover_near,
    cwr_proxy,
  };
}
